import { useEffect, useRef, useState } from "react";
import { productSearchService } from "../services/productSearchService";
import type { ManagedStrategySearchItem } from "../types/productV3";

export interface UseAdminManagedStrategiesResult {
  managedById: ReadonlyMap<number, ManagedStrategySearchItem>;
  /** Ids that could not be resolved. Drives the caller's incomplete-data warning. */
  failedIds: number[];
  isLoading: boolean;
}

const EMPTY_MAP: ReadonlyMap<number, ManagedStrategySearchItem> = new Map();
const EMPTY_IDS: number[] = [];

function normalizeIds(strategyIds: number[]): number[] {
  return Array.from(new Set(strategyIds)).sort((a, b) => a - b);
}

/**
 * Resolves managed strategies by id through the admin product-search route
 * (all visibilities). One id failing never fails the batch: it lands in
 * `failedIds` so the caller can warn instead of silently understating a book.
 */
export function useAdminManagedStrategies(
  strategyIds: number[],
  enabled: boolean,
  /**
   * Changing this value re-runs the fetch for the same ids. Callers with a
   * Refresh control pass an incrementing token so a manual refresh also picks
   * up a re-activated composition; callers without one omit it and keep the
   * fetch-once-per-id-set behaviour.
   */
  reloadToken = 0
): UseAdminManagedStrategiesResult {
  const ids = normalizeIds(strategyIds);
  const idKey = ids.join(",");

  const [state, setState] = useState<{
    key: string | null;
    managedById: ReadonlyMap<number, ManagedStrategySearchItem>;
    failedIds: number[];
    isLoading: boolean;
  }>({ key: null, managedById: EMPTY_MAP, failedIds: EMPTY_IDS, isLoading: false });

  // Monotonic token: this hook is keyed to a user-changeable selection (the
  // admin picks a row), so a slower earlier request must never write over a
  // newer one -- on the success path, the failure path, or the loading reset.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const loadKey = `${enabled ? 1 : 0}|${idKey}|${reloadToken}`;

    if (!enabled || idKey === "") {
      setState({
        key: loadKey,
        managedById: EMPTY_MAP,
        failedIds: EMPTY_IDS,
        isLoading: false,
      });
      return () => controller.abort();
    }

    const requestedIds = idKey.split(",").map(Number);
    // A non-positive or non-integer id can never resolve (the search route
    // requires productId >= 1). Fail it locally rather than spending a
    // guaranteed 400 on it.
    const fetchableIds = requestedIds.filter(id => Number.isInteger(id) && id > 0);
    const unfetchableIds = requestedIds.filter(id => !Number.isInteger(id) || id <= 0);

    setState({
      key: loadKey,
      managedById: EMPTY_MAP,
      failedIds: EMPTY_IDS,
      isLoading: true,
    });

    void (async () => {
      const resolved = new Map<number, ManagedStrategySearchItem>();
      const failed: number[] = [...unfetchableIds];

      await Promise.all(
        fetchableIds.map(async id => {
          try {
            const item = await productSearchService.getManagedStrategyForAdmin(
              id,
              controller.signal
            );
            if (controller.signal.aborted) return;
            resolved.set(id, item);
          } catch {
            if (controller.signal.aborted) return;
            failed.push(id);
          }
        })
      );

      if (controller.signal.aborted || requestId !== requestIdRef.current) return;
      setState({
        key: loadKey,
        managedById: resolved,
        failedIds: failed.sort((a, b) => a - b),
        isLoading: false,
      });
    })();

    return () => controller.abort();
  }, [idKey, enabled, reloadToken]);

  // Until this render's key has been committed, report the pending state rather
  // than a previous selection's resolved strategies.
  const currentKey = `${enabled ? 1 : 0}|${idKey}|${reloadToken}`;
  if (state.key !== currentKey) {
    return {
      managedById: EMPTY_MAP,
      failedIds: EMPTY_IDS,
      isLoading: enabled && idKey !== "",
    };
  }

  return {
    managedById: state.managedById,
    failedIds: state.failedIds,
    isLoading: state.isLoading,
  };
}
